import * as React from "react"
import { Check, ChevronsUpDown, X } from "lucide-react"

import { Button } from "../ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "../ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover"
import { cn } from "./utils"

interface SearchableSelectProps {
  options: { value: string; label: string }[]
  placeholder?: string
  value: string
  onChange: (value: string) => void
  className?: string
  searchPlaceholder?: string
  emptyText?: string
}

export function SearchableSelect({
  options,
  placeholder = "Select an option",
  value,
  onChange,
  className,
  searchPlaceholder = "Search...",
  emptyText = "No results found.",
}: SearchableSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")

  const selectedLabel =
    options.find((opt) => opt.value === value)?.label || placeholder

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-[240px] justify-between bg-black/20 border-white/10 text-white hover:bg-black/30 hover:text-white",
            className
          )}
        >
          <span className="truncate">{selectedLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 opacity-60" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className="w-[240px] p-0 bg-slate-900 text-white border border-white/10"
      >
        <Command className="bg-slate-900 text-white">
          {/* Search input with clear */}
          <div className="relative">
            <CommandInput
              value={search}
              onValueChange={setSearch}
              placeholder={searchPlaceholder}
              className="text-white placeholder:text-slate-400 pr-8"
            />

            {search && (
              <X
                className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 hover:text-white cursor-pointer"
                onClick={() => setSearch("")}
              />
            )}
          </div>

          <CommandEmpty className="px-2 py-1.5 text-sm text-slate-400">
            {emptyText}
          </CommandEmpty>

          <CommandGroup className="max-h-[220px] overflow-y-auto">
            {options.map((option) => (
              <CommandItem
                key={option.value}
                value={option.value}
                onSelect={(currentValue) => {
                  onChange(currentValue)
                  setOpen(false)
                  setSearch("") // reset search after select
                }}
                className={cn(
                  "cursor-pointer text-white hover:bg-white/10",
                  value === option.value && "bg-white/10"
                )}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    value === option.value ? "opacity-100" : "opacity-0"
                  )}
                />
                <span className="truncate">{option.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
